// ============================================================================
// colorRefs.ts — РЕЗОЛВ ССЫЛОК НА ЦВЕТА (единый механизм, SRP/SSoT):
// значение цвета в теме/стиле может быть:
//   '#RRGGBB'        — hex (непосредственно)
//   '@имя'           — ссылка на цвет из СПРАВОЧНИКА ЦВЕТОВ (metadata_colors,
//                      SSoT — сервер): «primary = @Бежевый», «стиль «Победа» →
//                      @Зелёный». Смена цвета в справочнике меняет всё, что
//                      на него ссылается.
//   'имя слота'      — слот ТЕМЫ (var(--color-<слот>), один цвет в разных
//                      темах разный) — резолвится в CSS-переменную.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { createLogger } from '@/lib/logger';

const log = createLogger('colorRefs');

let colorMapPromise: Promise<Record<string, string>> | null = null;

/** Карта «код цвета → hex» из справочника (кэш; SSoT — сервер).
 *  ГЕЙТ базы: справочник цветов — в схеме базы; без baseSlot пустая карта
 *  (лаунчер), ошибка не кэшируется навсегда. */
export function getColorMap(): Promise<Record<string, string>> {
  if (!useCenterStore.getState().baseSlug) {
    if (colorMapPromise) colorMapPromise = null;
    return Promise.resolve({});
  }
  if (!colorMapPromise) {
    colorMapPromise = getSDK().metadata.listColors({})
      .then((r: any) => {
        const map: Record<string, string> = {};
        for (const c of (r?.items || [])) {
          if (c?.name && c?.value) map[String(c.name)] = String(c.value);
        }
        return map;
      })
      .catch((e: any) => {
        // Ошибка не кэшируется — повтор при следующем обращении
        colorMapPromise = null;
        log.debug('colors load failed', e);
        return {};
      });
  }
  return colorMapPromise;
}

/** Сброс кэша (после изменения справочника цветов). */
export function invalidateColorMap(): void {
  colorMapPromise = null;
}

/**
 * Резолв значения цвета в hex (или '' если не резолвится).
 * - '#RRGGBB' → как есть
 * - '@имя' → hex из справочника цветов (нет в справочнике → '')
 * - иначе → '' (это слот темы — резолвится в var(--color-<слот>) отдельно)
 */
export function colorRefToHex(value: string | undefined, colorMap?: Record<string, string>): string {
  if (!value) return '';
  const v = String(value).trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  if (v.startsWith('@')) {
    const name = v.slice(1);
    if (colorMap && colorMap[name]) return colorMap[name];
    return '';
  }
  return '';
}

/** Значение ссылки: hex или CSS-переменная слота темы. */
export function colorRefToCss(value: string | undefined, colorMap?: Record<string, string>): string {
  const hex = colorRefToHex(value, colorMap);
  if (hex) return hex;
  const v = String(value || '').trim();
  if (!v || v.startsWith('@')) return '';
  return `var(--color-${v})`;
}

/** Читаемый цвет текста на фоне (тёмный/светлый по яркости) — единая функция.
 *  Порог 0.5: оранжевые/светлые фоны (Ayu: #E8830C/#FF8F40/#FF5D5D) получают
 *  ТЁМНЫЙ текст (контраст > 7), тёмно-красные (#F51818) — белый. */
export function contrastText(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.5 ? '#18181b' : '#ffffff';
}

/** Резолв значения слота темы в hex: hex напрямую или '@имя' из справочника. */
export function slotHex(value: string | undefined, colorMap?: Record<string, string>): string {
  const hex = colorRefToHex(value, colorMap);
  if (hex) return hex;
  const v = String(value || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v : '';
}
